import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["form"]
  
  connect() {
    this.checkAllForms()
    this.setupEventListeners()
    this.checkAllFormsAndFields()
  }
  
  disconnect() {
    this.removeEventListeners()
  }
  
  setupEventListeners() {
    this.boundCheckForm = this.checkForm.bind(this)
    this.boundCheckAllForms = this.checkAllForms.bind(this)
    this.boundHandleModalShow = this.handleModalShow.bind(this)
    this.boundHandleModalShown = this.handleModalShown.bind(this)
    this.boundHandleModalInput = this.handleModalInput.bind(this)
    this.boundHandleModalHidden = this.handleModalHidden.bind(this)
    this.boundHandleTurboFrameLoad = this.handleTurboFrameLoad.bind(this)
    this.boundHandleTurboFrameRender = this.handleTurboFrameRender.bind(this)
    this.boundHandleTurboBeforeStreamRender = this.handleTurboBeforeStreamRender.bind(this)
    
    this.element.addEventListener("input", this.boundCheckAllForms)
    this.element.addEventListener("change", this.boundCheckAllForms)
    
    document.addEventListener("turbo:frame-load", this.boundHandleTurboFrameLoad)
    document.addEventListener("turbo:frame-render", this.boundHandleTurboFrameRender)
    document.addEventListener("turbo:before-stream-render", this.boundHandleTurboBeforeStreamRender)
    
    this.element.addEventListener("show.bs.modal", this.boundHandleModalShow)
    this.element.addEventListener("shown.bs.modal", this.boundHandleModalShown)
    this.element.addEventListener("hidden.bs.modal", this.boundHandleModalHidden)
    
    document.addEventListener("input", this.boundHandleModalInput)
    document.addEventListener("change", this.boundHandleModalInput)
  }
  
  removeEventListeners() {
    this.element.removeEventListener("input", this.boundCheckAllForms)
    this.element.removeEventListener("change", this.boundCheckAllForms)
    document.removeEventListener("turbo:frame-load", this.boundHandleTurboFrameLoad)
    document.removeEventListener("turbo:frame-render", this.boundHandleTurboFrameRender)
    document.removeEventListener("turbo:before-stream-render", this.boundHandleTurboBeforeStreamRender)
    this.element.removeEventListener("show.bs.modal", this.boundHandleModalShow)
    this.element.removeEventListener("shown.bs.modal", this.boundHandleModalShown)
    this.element.removeEventListener("hidden.bs.modal", this.boundHandleModalHidden)
    document.removeEventListener("input", this.boundHandleModalInput)
    document.removeEventListener("change", this.boundHandleModalInput)
  }
  
  handleTurboBeforeStreamRender(event) {
    setTimeout(() => {
      const forms = this.element.querySelectorAll(".mandatory-fields-checker")
      forms.forEach(form => this.checkForm(form))
      this.checkAllFormsAndFields()
    }, 300)
  }
  
  handleTurboFrameLoad(event) {
    const frame = event.target
    if (this.element.contains(frame)) {
      setTimeout(() => {
        const forms = this.element.querySelectorAll(".mandatory-fields-checker")
        forms.forEach(form => this.checkForm(form))
        this.checkAllFormsAndFields()
      }, 200)
    }
  }
  
  handleTurboFrameRender(event) {
    const frame = event.target
    if (this.element.contains(frame)) {
      setTimeout(() => {
        const forms = this.element.querySelectorAll(".mandatory-fields-checker")
        forms.forEach(form => this.checkForm(form))
        this.checkAllFormsAndFields()
      }, 300)
    }
  }
  
  handleModalShow(event) {
    const modal = event.target
    const parentForm = modal.closest(".mandatory-fields-checker")
    if (parentForm) {
      parentForm.dataset.modalOpen = "true"
    }
  }
  
  handleModalShown(event) {
    const modal = event.target
    const parentForm = modal.closest(".mandatory-fields-checker")
    if (parentForm) {
      setTimeout(() => {
        this.checkModalFields(modal)
        this.checkFormIncludingModal(parentForm)
        this.checkAllFormsAndFields()
      }, 150)
    }
  }
  
  handleModalHidden(event) {
    const modal = event.target
    const parentForm = modal.closest(".mandatory-fields-checker")
    if (parentForm) {
      delete parentForm.dataset.modalOpen
      this.clearModalErrorStates(modal)
      
      setTimeout(() => {
        this.checkFormIncludingModal(parentForm)
        this.checkAllFormsAndFields()
      }, 50)
    }
  }
  
  clearModalErrorStates(modal) {
    const modalFormGroups = modal.querySelectorAll(".form-group.has-error")
    modalFormGroups.forEach(group => {
      group.classList.remove("has-error")
    })
  }
  
  checkModalFields(modal) {
    const modalInputs = modal.querySelectorAll("input, select, textarea")
    
    modalInputs.forEach(input => {
      if (input.type === "hidden") {
        return
      }
      
      const isRequired = this.isRequiredField(input)
      const isEmpty = this.isFieldEmpty(input)
      const formGroup = input.closest(".form-group")
      
      if (formGroup) {
        if (isRequired && isEmpty) {
          formGroup.classList.add("has-error")
        } else {
          formGroup.classList.remove("has-error")
        }
      }
    })
  }
  
  handleModalInput(event) {
    const input = event.target
    const modal = input.closest(".modal")
    if (modal) {
      const parentForm = modal.closest(".mandatory-fields-checker")
      if (parentForm && this.element.contains(parentForm)) {
        clearTimeout(this.modalInputTimeout)
        this.modalInputTimeout = setTimeout(() => {
          this.checkModalFields(modal)
          this.checkFormIncludingModal(parentForm)
          this.checkAllFormsAndFields()
        }, 100)
      }
    }
  }
  
  checkAllForms() {
    const lineItemForms = this.element.querySelectorAll(".mandatory-fields-checker")
    lineItemForms.forEach(form => this.checkForm(form))
    this.checkAllFormsAndFields()
  }
  
  checkForm(form) {
    const hasUnfilledMandatoryFields = this.hasUnfilledMandatoryFields(form)
    
    if (hasUnfilledMandatoryFields) {
      this.highlightForm(form)
    } else {
      this.removeHighlight(form)
    }
    
    this.updateBootstrap3ErrorStates(form)
  }
  
  checkFormIncludingModal(form) {
    const hasUnfilledMandatoryFields = this.hasUnfilledMandatoryFieldsIncludingModal(form)
    
    if (hasUnfilledMandatoryFields) {
      this.highlightForm(form)
    } else {
      this.removeHighlight(form)
    }
    
    this.updateBootstrap3ErrorStatesIncludingModal(form)
  }
  
  hasUnfilledMandatoryFields(form) {
    const allInputs = form.querySelectorAll("input, select, textarea")
    
    for (let input of allInputs) {
      if (input.disabled || input.type === "hidden") {
        continue
      }
      
      if (this.isRequiredField(input) && this.isFieldEmpty(input)) {
        return true
      }
    }
    
    return false
  }
  
  hasUnfilledMandatoryFieldsIncludingModal(form) {
    const allInputs = Array.from(form.querySelectorAll("input, select, textarea"))
      .filter(input => input.closest(".mandatory-fields-checker") === form)
    
    const fieldGroups = new Map()
    
    allInputs.forEach(input => {
      if (input.type === "hidden") {
        return
      }
      
      const name = input.name
      if (!fieldGroups.has(name)) {
        fieldGroups.set(name, [])
      }
      fieldGroups.get(name).push(input)
    })
    
    for (let [fieldName, inputs] of fieldGroups) {
      const requiredInputs = inputs.filter(input => this.isRequiredField(input))
      if (requiredInputs.length === 0) {
        continue
      }
      
      let hasValue = false
      
      for (let input of requiredInputs) {
        if (!this.isFieldEmpty(input)) {
          hasValue = true
          break
        }
      }
      
      if (!hasValue) {
        return true
      }
    }
    
    return false
  }
  
  isRequiredField(input) {
    // Skip metadata fields
    if (input.name && input.name.match(/\[(id|_destroy|created_at|updated_at|key)\]$/)) {
      return false
    }
    
    // Check for HTML5 required attribute
    if (input.hasAttribute("required")) {
      return true
    }
    
    // Check for data-required attribute
    if (input.dataset.required === "true") {
      return true
    }
    
    // Check for label with "required" class or asterisk
    const formGroup = input.closest(".form-group, .field")
    if (formGroup) {
      const label = formGroup.querySelector("label")
      if (label) {
        if (label.classList.contains("required")) {
          return true
        }
        
        if (label.textContent && label.textContent.includes("*")) {
          return true
        }
        
        if (label.querySelector(".required")) {
          return true
        }
      }
    }
    
    return false
  }
  
  isFieldEmpty(input) {
    const value = input.value?.trim() || ""
    
    switch (input.type) {
      case "checkbox":
      case "radio":
        const name = input.name
        const form = input.closest("form") || input.closest(".modal") || document
        const checkedInputs = form.querySelectorAll(`input[name="${name}"]:checked`)
        return checkedInputs.length === 0
      
      case "select-one":
      case "select-multiple":
        return !input.value || input.value === ""
      
      default:
        return value === ""
    }
  }
  
  highlightForm(form) {
    form.classList.add("unfocused-mandatory-fields")
    form.classList.add("has-unfilled-required-fields")
    this.addRequiredFieldsIndicator(form)
  }
  
  removeHighlight(form) {
    form.classList.remove("unfocused-mandatory-fields")
    form.classList.remove("has-unfilled-required-fields")
    this.removeRequiredFieldsIndicator(form)
    this.resetFormToCleanState(form)
  }
  
  updateBootstrap3ErrorStates(form) {
    const allInputs = form.querySelectorAll("input, select, textarea")
    
    allInputs.forEach(input => {
      if (input.disabled || input.type === "hidden") {
        return
      }
      
      const modal = input.closest(".modal")
      if (modal && form.dataset.modalOpen !== "true") {
        return
      }
      
      const formGroup = input.closest(".form-group")
      if (formGroup) {
        if (this.isRequiredField(input) && this.isFieldEmpty(input)) {
          formGroup.classList.add("has-error")
        } else {
          formGroup.classList.remove("has-error")
        }
      }
    })
  }
  
  updateBootstrap3ErrorStatesIncludingModal(form) {
    const allInputs = form.querySelectorAll("input, select, textarea")
    
    allInputs.forEach(input => {
      if (input.type === "hidden") {
        return
      }
      
      const formGroup = input.closest(".form-group")
      if (formGroup) {
        const modal = input.closest(".modal")
        if (modal && form.dataset.modalOpen !== "true") {
          formGroup.classList.remove("has-error")
          return
        }
        
        if (this.isRequiredField(input) && this.isFieldEmpty(input)) {
          formGroup.classList.add("has-error")
        } else {
          formGroup.classList.remove("has-error")
        }
      }
    })
  }
  
  removeBootstrap3ErrorStates(form) {
    const formGroups = form.querySelectorAll(".form-group.has-error")
    formGroups.forEach(group => {
      group.classList.remove("has-error")
    })
  }
  
  resetFormToCleanState(form) {
    this.removeBootstrap3ErrorStates(form)
    form.style.backgroundColor = ""
    form.style.border = ""
  }
  
  addRequiredFieldsIndicator(form) {
    if (form.querySelector(".required-fields-warning")) {
      return
    }
    
    const indicator = document.createElement("div")
    indicator.className = "required-fields-warning"
    
    const warningText = this.element.dataset.completeRequiredText || 
                       "Please complete all required fields before proceeding."
    
    indicator.innerHTML = `
      <i class="fa fa-exclamation-triangle"></i>
      ${warningText}
    `
    
    const firstRow = form.querySelector(".row")
    if (firstRow) {
      form.insertBefore(indicator, firstRow)
    } else {
      form.insertBefore(indicator, form.firstChild)
    }
  }
  
  removeRequiredFieldsIndicator(form) {
    const indicator = form.querySelector(".required-fields-warning")
    if (indicator) {
      indicator.remove()
    }
  }
  
  disableSubmitButton() {
    const submitButtons = this.element.querySelectorAll("button[type=\"submit\"], button.btn-success, input[type=\"submit\"]")
    
    submitButtons.forEach(button => {
      button.disabled = true
      button.classList.add("disabled")
      
      if (!button.getAttribute("data-original-title")) {
        const disabledText = this.element.dataset.submitDisabledText || 
                            "Please complete all required fields before submitting"
        button.setAttribute("data-original-title", button.title || "")
        button.title = disabledText
      }
    })
  }
  
  enableSubmitButton() {
    const submitButtons = this.element.querySelectorAll("button[type=\"submit\"], button.btn-success, input[type=\"submit\"]")
    
    submitButtons.forEach(button => {
      button.disabled = false
      button.classList.remove("disabled")
      
      const originalTitle = button.getAttribute("data-original-title")
      if (originalTitle !== null) {
        button.title = originalTitle
        button.removeAttribute("data-original-title")
      }
    })
  }
  
  checkAllFormsAndFields() {
    const lineItemForms = this.element.querySelectorAll(".mandatory-fields-checker")
    let hasUnfilledLineItemFields = false
    
    lineItemForms.forEach(form => {
      if (this.hasUnfilledMandatoryFieldsIncludingModal(form)) {
        hasUnfilledLineItemFields = true
      }
    })
    
    const mainFormInputs = this.element.querySelectorAll("input, select, textarea")
    let hasUnfilledMainFormFields = false
    
    for (let input of mainFormInputs) {
      if (input.disabled || input.type === "hidden") {
        continue
      }
      
      if (this.isRequiredField(input) && this.isFieldEmpty(input)) {
        hasUnfilledMainFormFields = true
        break
      }
    }
    
    if (hasUnfilledLineItemFields || hasUnfilledMainFormFields) {
      this.disableSubmitButton()
    } else {
      this.enableSubmitButton()
    }
  }
  
  refresh() {
    this.checkAllForms()
  }
}
